import { Button, Text } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { PlusIcon } from "@phosphor-icons/react";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SECTION_IDS } from "../../utils/invoice-steps";
import { InvoiceAttachmentForm } from "./invoice-attachment-form";
import { InvoiceProductForm, type InvoiceProductFormHandle } from "./invoice-product-form";
import productClasses from "./invoice-product-form.module.css";
import { InvoiceReceiverForm } from "./invoice-receiver-form";
import { InvoiceSectionCard } from "./invoice-section-card";
import { InvoiceSenderForm } from "./invoice-sender-form";

type Props = {
  form: UseFormReturnType<any>;
  activeStep: number;
  completions: boolean[];
  productFormRef: React.RefObject<InvoiceProductFormHandle | null>;
};

export function InvoiceSectionList({ form, activeStep, completions, productFormRef }: Props) {
  const { t } = useTranslation(["invoice", "common"]);

  const lineCount =
    (form.values?.lines?.filter((l: any) => !l?._removed)?.length || 0) +
    (form.values?.additionalLines?.filter((l: any) => !l?._removed)?.length || 0);

  const buyerSelected = !!form.values?.receiverBusinessId;

  const sections = useMemo(
    () => [
      {
        title: t("invoice:step.buyer"),
        hint: t("invoice:step.buyerHint"),
        flush: false,
        badge: undefined as React.ReactNode,
        action: undefined as React.ReactNode,
        children: <InvoiceReceiverForm form={form} />,
      },
      {
        title: t("invoice:step.sender"),
        hint: undefined,
        flush: false,
        badge: undefined,
        action: undefined,
        children: <InvoiceSenderForm form={form} />,
      },
      {
        title: t("invoice:step.items"),
        hint: !buyerSelected ? (
          <Text span fz="sm" c="dimmed">
            {t("invoice:productForm.selectBuyerFirst")}
          </Text>
        ) : undefined,
        flush: true,
        badge: t("invoice:productForm.lineCountPill", { count: lineCount }),
        action: (
          <>
            <Button
              className={productClasses.sectionAction}
              size="sm"
              variant="default"
              leftSection={<PlusIcon size={14} />}
              disabled={!buyerSelected}
              onClick={() => productFormRef.current?.openAddManual()}
            >
              {t("invoice:productForm.addManualLine")}
            </Button>
            <Button
              className={productClasses.sectionAction}
              size="sm"
              variant={buyerSelected ? "light" : "default"}
              color={buyerSelected ? "brand" : "gray"}
              leftSection={<PlusIcon size={14} />}
              disabled={!buyerSelected}
              onClick={() => productFormRef.current?.openAdd()}
            >
              {t("invoice:productForm.addItem")}
            </Button>
          </>
        ),
        children: <InvoiceProductForm ref={productFormRef} form={form} hideSummary />,
      },
      {
        title: t("invoice:step.attachments"),
        hint: t("invoice:step.attachmentsOptional"),
        flush: false,
        badge: undefined,
        action: undefined,
        children: <InvoiceAttachmentForm values={form.values} setFieldValue={form.setFieldValue} />,
      },
    ],
    [buyerSelected, form, lineCount, productFormRef, t],
  );

  return (
    <>
      {sections.map((section, index) => (
        <InvoiceSectionCard
          key={SECTION_IDS[index]}
          id={SECTION_IDS[index]}
          title={section.title}
          hint={section.hint}
          stepNumber={index + 1}
          isActive={activeStep === index}
          isCompleted={completions[index]}
          badge={section.badge}
          action={section.action}
          flush={section.flush}
        >
          {section.children}
        </InvoiceSectionCard>
      ))}
    </>
  );
}
